import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import UpdateCollectiveAgreementModal from "../../components/CollectiveAgreement/UpdateCollectiveAgreementModal";
import Flex from "../../components/Layout/Flex";
import ActionsPopOver, {
	PopOverAction,
} from "../../components/Shared/ActionsPopover/ActionsPopover";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import CollectiveAgreementForm from "../../components/Shared/CollectiveAgreementForm/CollectiveAgreementForm";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { ConsultantCollectiveAgreementsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { useSidebarStore } from "../../hooks/sidebar.hooks";
import {
	CreateCollectiveAgreementRequest,
	useCollectiveAgreement,
	useUpdateCollectiveAgreement,
} from "../../networking/collective-agreement.hooks";
import { CollectiveAgreementApiSchema } from "../../schemas/collectiveAgreement.schema";
import { CollectiveAgreementRemoveConfirmModal } from "./CollectiveAgreementRemoveConfirmModal";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

const CollectiveAgreementEditPage: React.FC = () => {
	const modalStore = useConfirmModalStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const sidebarStore = useSidebarStore();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
	const [pendingFormData, setPendingFormData] = useState<CreateCollectiveAgreementRequest | null>(
		null
	);

	const { collectiveAgreement, isLoading: isLoadingAgreement } = useCollectiveAgreement(id);
	const updateCollectiveAgreement = useUpdateCollectiveAgreement();

	const form = useForm<CreateCollectiveAgreementRequest>({
		mode: "onBlur",
		resolver: zodResolver(CollectiveAgreementApiSchema),
	});

	const { reset } = form;

	// Load initial data when agreement is fetched
	useEffect(() => {
		if (collectiveAgreement) {
			reset({
				name: collectiveAgreement.name,
				internalDescription: collectiveAgreement.internalDescription || "",
				employmentType: collectiveAgreement.employmentType,
				pensionType: collectiveAgreement.pensionType,
				unionType: collectiveAgreement.unionType,
				vacationSalaryPercent: collectiveAgreement.vacationSalaryPercent,
				isGflControlled: collectiveAgreement.isGflControlled,
				guaranteeSalary: collectiveAgreement.guaranteeSalary,
				guaranteedHoursPerMonth: collectiveAgreement.guaranteedHoursPerMonth,
				guaranteedHoursByMonth: collectiveAgreement.guaranteedHoursByMonth || {
					1: 0,
					2: 0,
					3: 0,
					4: 0,
					5: 0,
					6: 0,
					7: 0,
					8: 0,
					9: 0,
					10: 0,
					11: 0,
					12: 0,
				},
				productionUnit: collectiveAgreement.productionUnit || "",
				salaryProgramCategory: collectiveAgreement.salaryProgramCategory || "",
				itpCostCenter: collectiveAgreement.itpCostCenter || "",
				itpPlanId: collectiveAgreement.itpPlanId || "",
				obCompensation: collectiveAgreement.obCompensation || [],
				salaryStatistics: {
					wageType: collectiveAgreement.salaryStatistics?.wageType || "",
					occupationCode: collectiveAgreement.salaryStatistics?.occupationCode || "",
					personnelCategory: collectiveAgreement.salaryStatistics?.personnelCategory || "",
					unionNumber: collectiveAgreement.salaryStatistics?.unionNumber || "",
					workTimeCode: collectiveAgreement.salaryStatistics?.workTimeCode || "",
					federalNumber: collectiveAgreement.salaryStatistics?.federalNumber || "",
					agreementCode: collectiveAgreement.salaryStatistics?.agreementCode || "",
					workplaceNumberSn: collectiveAgreement.salaryStatistics?.workplaceNumberSn || "",
					workplaceNumberScb: collectiveAgreement.salaryStatistics?.workplaceNumberScb || "",
				},
			});
		}
	}, [collectiveAgreement, reset]);

	const handleSubmit = async (formData: CreateCollectiveAgreementRequest) => {
		if (!id) return;

		// Check if guaranteedHoursByMonth or guaranteeSalary has changed
		const hasGuaranteedHoursChanged =
			collectiveAgreement &&
			formData.guaranteedHoursByMonth &&
			JSON.stringify(formData.guaranteedHoursByMonth) !==
				JSON.stringify(collectiveAgreement.guaranteedHoursByMonth);

		const hasGuaranteeSalaryChanged =
			collectiveAgreement && formData.guaranteeSalary !== collectiveAgreement.guaranteeSalary;

		if (hasGuaranteedHoursChanged || hasGuaranteeSalaryChanged) {
			// Show modal to inform user about affected consultants
			setPendingFormData(formData);
			setShowBulkUpdateModal(true);
			return;
		}

		// No change in guaranteed hours/salary, proceed with normal update
		await performUpdate(formData);
	};

	const performUpdate = async (formData: CreateCollectiveAgreementRequest) => {
		if (!id) return;

		try {
			await updateCollectiveAgreement.mutateAsync({
				agreementId: id,
				update: formData,
			});
		} catch (error) {
			// Error handling is done in the mutation hook
			console.error("Failed to update collective agreement:", error);
		}
	};

	const handleBulkUpdateModalConfirm = async () => {
		setShowBulkUpdateModal(false);

		if (pendingFormData) {
			// Update the collective agreement (backend will auto-propagate)
			await performUpdate(pendingFormData);
			setPendingFormData(null);
		}
	};

	const handleBulkUpdateModalCancel = () => {
		setShowBulkUpdateModal(false);
		setPendingFormData(null);
	};

	const breadcrumbs = [
		...ConsultantCollectiveAgreementsBreadcrumbs,
		{
			title: collectiveAgreement?.name || "...",
			path: "",
		},
	];

	const handleShowAssignedConsultants = () => {
		if (id) {
			sidebarStore.openSidebar("ASSIGNED_CONSULTANTS", {
				collectiveAgreementId: id,
			});
		}
	};

	const actions: PopOverAction[] = [
		{
			text: t("collectiveAgreement.showAssignedConsultants"),
			action: handleShowAssignedConsultants,
		},
		{
			text: "REMOVE",
			textStyles: { color: "danger" },
			action: () => setShowDeleteModal(true),
		},
	];

	if (isLoadingAgreement) {
		return (
			<>
				<Breadcrumbs items={breadcrumbs} />
				<div className="p-4 px-6">
					<Flex margin="4rem auto 4rem auto" direction="column">
						<LoadingIndicator />
					</Flex>
				</div>
			</>
		);
	}

	if (!collectiveAgreement) {
		return (
			<>
				<Breadcrumbs items={breadcrumbs} />
				<div className="p-4 px-6">
					<Flex margin="4rem auto 4rem auto" direction="column">
						<p>{"Kollektivavtalet hittades inte"}</p>
					</Flex>
				</div>
			</>
		);
	}

	return (
		<>
			<Breadcrumbs items={breadcrumbs} />
			<div className="p-4 px-6">
				<Flex justifyContent="flex-end">
					<ActionsPopOver actions={actions} />
				</Flex>
				<Flex margin="1rem auto 0 auto" direction="column">
					<CollectiveAgreementForm
						form={form}
						onSubmit={handleSubmit}
						isLoading={updateCollectiveAgreement.isLoading}
						submitButtonText={"Uppdatera kollektivavtal"}
						initialValues={collectiveAgreement}
					/>
				</Flex>
				{showDeleteModal && (
					<CollectiveAgreementRemoveConfirmModal
						agreementId={id!}
						onCancel={() => setShowDeleteModal(false)}
						onSubmit={() => navigate(routes.AGREEMENT)}
					/>
				)}
				{showBulkUpdateModal && (
					<UpdateCollectiveAgreementModal
						collectiveAgreementId={id!}
						onConfirm={handleBulkUpdateModalConfirm}
						onCancel={handleBulkUpdateModalCancel}
					/>
				)}
			</div>
		</>
	);
};

export default CollectiveAgreementEditPage;
